/**
 * Stock Module API endpoints.
 *
 * Provides API functions and React Query hooks for the Stock module:
 * - Balances: current stock levels, history, alerts
 * - Movements: stock movement log, manual adjustments
 * - Receipts: supplier deliveries
 * - Transfers: inter-location transfers
 * - Write-offs: stock write-offs
 * - Supplier Returns: returns to suppliers
 * - Productions: recipe-based production
 * - Inventories: physical inventory counts
 * - B2B: B2B customers, price lists, sales
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-client';

import { api } from './client';
import { createStockB2BSalesApi, type B2BSaleInvoicing } from './stock/b2bSales';
import { createStockB2BShipmentsApi } from './stock/b2bShipments';
import type {
  CursorListResponse,
  ListResponse,
  MovementType,
  SuccessResponse,
  StockAuditEvent,
  PaginationParams,
  StockMovement,
} from './stock/common';
import {
  createStockDocumentMutationCache,
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationOptions,
} from './stock/documentMutationCache';
import { hotReportsApi } from './stock/hotReports';
import { createStockIngredientsApi } from './stock/ingredients';
import { createStockInventoriesApi, type UpdateInventoryItemRequest } from './stock/inventories';
import { createStockLaborRatesApi } from './stock/laborRates';
import { createStockMaterialsApi } from './stock/materials';
import { createStockOverheadRatesApi } from './stock/overheadRates';
import { platformSalesApi } from './stock/platformSales';
import { createStockProductsApi } from './stock/products';
import { createStockReceiptsApi } from './stock/receipts';
import { createStockRecipesApi } from './stock/recipes';
import { salesInvoicesApi } from './stock/salesInvoices';
import { createStockSellerWorkspaceApi } from './stock/sellerWorkspace';
import { createStockSupplierProductMappingsApi } from './stock/supplierProductMappings';
import { createStockSuppliesApi } from './stock/supplies';
import { createStockTransfersApi } from './stock/transfers';

export * from './stock/b2bSales';
export * from './stock/common';
export * from './stock/b2bShipments';
export * from './stock/inventories';
export * from './stock/ingredients';
export * from './stock/laborRates';
export * from './stock/overheadRates';
export * from './stock/materials';
export * from './stock/hotReports';
export * from './stock/platformSales';
export * from './stock/supplies';
export * from './stock/products';
export * from './stock/receipts';
export * from './stock/recipes';
export * from './stock/salesInvoices';
export * from './stock/sellerWorkspace';
export type { StockDocumentMutationOptions } from './stock/documentMutationCache';
export * from './stock/supplierProductMappings';
export * from './stock/transfers';

type StockDocumentEntity =
  | 'receipt'
  | 'transfer'
  | 'write_off'
  | 'supplier_return'
  | 'production'
  | 'recipe'
  | 'ingredient'
  | 'supply'
  | 'inventory'
  | 'b2b_sale'
  | 'b2b_shipment';

const { rememberStockDocumentUpdatedAt, forgetStockDocumentUpdatedAt, withExpectedUpdatedAt } =
  createStockDocumentMutationCache<StockDocumentEntity>();

// =============================================================================
// Common Types
// =============================================================================

/** Write-off reason enum */
export type WriteOffReason =
  | 'spoilage'
  | 'expired'
  | 'damage'
  | 'theft'
  | 'other'
  | 'staff_meal'
  | 'promo_giveaway'
  | 'own_use'
  | 'tasting'
  | 'hot_leftover'
  | 'not_for_sale';

/** Production status */
export type ProductionStatus = 'draft' | 'confirmed';

/** Supplier return status */
export type SupplierReturnStatus = 'draft' | 'confirmed';

// =============================================================================
// Movement Types
// =============================================================================

export interface SalesStatsLocation {
  location_id: string;
  /** Net sold quantity for the period (positive decimal string). */
  sales_qty: string;
  /** Number of days in the range when the store had any sales activity (any product). */
  sales_days: number;
  /** Average daily sold quantity for this product within the store's sales days (positive decimal string). */
  avg_daily_sales_qty?: string | null;
  /** Days within the requested range excluded from avg (no sales at all in the store). */
  excluded_dates: string[];
}

export interface SalesStatsResponse {
  request_id: string;
  product_id: string;
  date_from: string;
  date_to: string;
  range_days: number;
  /** Net sold quantity for the period (positive decimal string). */
  total_sales_qty: string;
  /** Overall avg daily sold quantity (sum of per-store averages). */
  avg_daily_sales_qty?: string | null;
  by_location: SalesStatsLocation[];
}

export interface ProductSalesDashboardKpi {
  total_qty: string;
  total_revenue: string;
  avg_unit_price: string;
  pos_doc_count: number;
  b2b_doc_count: number;
  avg_daily_qty: string;
  qty_delta_pct?: number | null;
  revenue_delta_pct?: number | null;
  avg_price_delta_pct?: number | null;
  avg_daily_qty_delta_pct?: number | null;
}

export interface ProductSalesDashboardDailyItem {
  date: string;
  qty: string;
  revenue: string;
  doc_count: number;
}

export interface ProductSalesDashboardLocationItem {
  location_id: string;
  location_name: string;
  qty: string;
  revenue: string;
  avg_daily_qty: string;
  share_pct: number;
}

export interface ProductSalesDashboardRecentDocumentItem {
  date: string;
  entity_type: 'sale' | 'b2b_sale';
  document_id: string;
  location_name: string;
  qty: string;
  revenue: string;
}

export interface ProductSalesDashboardResponse {
  request_id: string;
  kpi: ProductSalesDashboardKpi;
  daily: ProductSalesDashboardDailyItem[];
  by_location: ProductSalesDashboardLocationItem[];
  recent_documents: ProductSalesDashboardRecentDocumentItem[];
}

export interface ProductProductionDashboardKpi {
  total_qty: string;
  doc_count: number;
  avg_batch_size: string;
  location_count: number;
  qty_delta_pct?: number | null;
  doc_count_delta_pct?: number | null;
  avg_batch_delta_pct?: number | null;
  cost_per_unit?: string | null;
  cost_per_unit_delta_pct?: number | null;
  reference_margin_pct?: number | null;
  reference_margin_delta_pct?: number | null;
  sparkline_qty?: number[] | null;
  sparkline_doc_count?: number[] | null;
  sparkline_avg_batch?: number[] | null;
  sparkline_cost?: number[] | null;
  sparkline_reference_margin?: number[] | null;
}

export interface ProductProductionDashboardDailyItem {
  date: string;
  qty: string;
  doc_count: number;
  prev_qty?: string | null;
}

export interface ProductProductionDashboardCostTrendItem {
  date: string;
  cost_per_unit: string;
  document_id: string;
}

export interface ProductProductionDashboardLocationItem {
  location_id: string;
  location_name: string;
  qty: string;
  doc_count: number;
  share_pct: number;
}

export interface ProductProductionDashboardVarianceItem {
  ingredient_product_id: string;
  ingredient_name: string;
  unit_code: string;
  recipe_qty: string;
  actual_qty: string;
  deviation_pct?: number | null;
}

export interface ProductProductionDashboardRecentDocumentItem {
  date: string;
  document_id: string;
  location_name: string;
  qty: string;
  status: string;
}

export interface ProductProductionDashboardResponse {
  request_id: string;
  kpi: ProductProductionDashboardKpi;
  daily: ProductProductionDashboardDailyItem[];
  by_location: ProductProductionDashboardLocationItem[];
  recipe_variance: ProductProductionDashboardVarianceItem[];
  recent_documents: ProductProductionDashboardRecentDocumentItem[];
  cost_trend: ProductProductionDashboardCostTrendItem[];
  reference_price?: string | null;
}

export interface MovementsDailySummaryItem {
  effective_date: string;
  incoming_qty: string;
  outgoing_qty: string;
  movement_count: number;
  inventory_adj_count?: number;
  /** Listed movements entered a day or more after their effective time. */
  backdated_count?: number;
  backdated_last_created_at?: string | null;
  /** Balance of the day and its parts; always over every movement type. */
  opening_balance?: string | null;
  closing_balance?: string | null;
  net_qty?: string;
  received_qty?: string;
  sold_qty?: string;
  written_off_qty?: string;
  inventory_adj_qty?: string;
}

/** Latest inventory count line of a product at a location. */
export interface MovementLastInventory {
  effective_at?: string | null;
  effective_date: string;
  created_at?: string | null;
  quantity: string;
  counted_quantity?: string | null;
}

export interface MovementsDailySummaryResponse {
  request_id: string;
  items: MovementsDailySummaryItem[];
  /** Only for a single location. */
  last_inventory?: MovementLastInventory | null;
  /** First day of the run of below-zero closings the period opens in. */
  negative_since?: string | null;
}

/** Movement filters */
export interface MovementFilters extends PaginationParams {
  [key: string]: string | number | boolean | string[] | undefined;
  location_id?: string;
  product_id?: string;
  movement_type?: string[] | string;
  date_from?: string;
  date_to?: string;
  include_cancelled?: boolean;
}

/** Movements response */
export type MovementsResponse = ListResponse<StockMovement>;

/** Manual correction request */
export interface ManualCorrectionRequest {
  product_id: string;
  location_id: string;
  quantity: string;
  /** ISO datetime (TIMESTAMPTZ). */
  effective_at: string;
  reason?: string;
}

export interface MovementDetailResponse {
  request_id: string;
  movement: StockMovement;
  current_quantity: string;
}

export interface MovementActionResponse {
  request_id: string;
  movement: StockMovement;
  already_cancelled?: boolean;
}

// =============================================================================
// Operations (Операции) Types (read-only)
// =============================================================================

export type StockOperationEntityType =
  | 'sale'
  | 'receipt'
  | 'transfer'
  | 'production'
  | 'write_off'
  | 'inventory'
  | 'supplier_return'
  | 'b2b_sale'
  | 'opening_balance'
  | 'platform_sale'
  | 'movement'
  | (string & {});

export interface StockOperationHeader {
  operation_id: string;
  entity_type: StockOperationEntityType;

  // Source identifiers (exactly one family is expected per operation)
  document_id?: string | null;
  revision_no?: number | null;
  gopos_order_id?: string | null;
  movement_id?: string | null;

  effective_at: string;
  /** Warsaw business date (YYYY-MM-DD) */
  effective_date: string;

  is_cancelled: boolean;

  movement_types: MovementType[];
  movement_count: number;

  // Location helpers for list UI
  location_id?: string | null;
  from_location_id?: string | null;
  to_location_id?: string | null;
  location_ids: string[];
}

export interface StockOperationLine {
  product_id: string;
  product_name?: string | null;
  quantity: string;
  unit_code: string;

  movement_type?: MovementType | null;
  location_id?: string | null;
  from_location_id?: string | null;
  to_location_id?: string | null;

  movement_ids?: string[] | null;
  is_cancelled?: boolean | null;
}

export interface StockOperationsListParams {
  include_cancelled?: boolean;
  limit?: number;
  cursor?: string | null;
  /** Effective date (Warsaw) range start (YYYY-MM-DD) */
  date_from?: string;
  /** Effective date (Warsaw) range end (YYYY-MM-DD) */
  date_to?: string;
  /** Search by ids / location / product name (server-side) */
  q?: string;
  /** Filter operations that include this location */
  location_id?: string;
  /** Filter operations that include this product */
  product_id?: string;
  /** Repeatable or comma-separated entity types */
  entity_type?: string[] | string;
}

export type StockOperationsListResponse = CursorListResponse<StockOperationHeader>;

export interface StockOperationDetailResponse {
  request_id: string;
  operation: StockOperationHeader;
  lines: StockOperationLine[];
}

export interface StockOperationsViewCounts {
  today: number;
  yesterday: number;
  last_7_days: number;
  last_30_days: number;
  all: number;
}

export interface StockOperationsViewCountsResponse {
  request_id: string;
  counts: StockOperationsViewCounts;
}

// =============================================================================
// Write-off Types
// =============================================================================

export type WriteOffStatus = 'draft' | 'confirmed';

export interface WriteOffItemPreview {
  product_id: string;
  quantity: string;
  unit_code: string;
  product_name: string;
}

/** Write-off document list item */
export interface WriteOffListItem {
  document_id: string;
  status: WriteOffStatus;
  current_revision_no?: number | null;
  current_revision_id?: string | null;
  /** ISO datetime (TIMESTAMPTZ). */
  write_off_at: string;
  /** Warsaw business date (YYYY-MM-DD). */
  effective_date: string;
  location_id: string;
  reason: WriteOffReason;
  notes?: string | null;
  items_count?: number | null;
  unit_totals?: Record<string, string> | null;
  estimated_total_amount?: string | null;
  valuation_cost_amount?: string | null;
  valuation_cost_is_partial?: boolean;
  valuation_cost_missing_items?: number;
  valuation_retail_amount?: string | null;
  valuation_retail_is_partial?: boolean;
  valuation_retail_missing_items?: number;
  items_preview?: WriteOffItemPreview[] | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_reason?: string | null;
}

export interface WriteOffItem {
  id: string;
  document_id: string;
  product_id: string;
  quantity: string;
  unit_code: string;
}

export interface WriteOffDocument extends WriteOffListItem {
  items: WriteOffItem[];
}

export interface WriteOffCurrentRevision {
  revision_id: string;
  revision_no: number;
}

export interface WriteOffRevisionMetadata {
  revision_id: string;
  revision_no: number;
  created_at: string;
  created_by?: string | null;
  change_reason?: string | null;
}

export interface WriteOffListSummary {
  by_status: Record<string, number>;
}

/** Write-off filters */
export interface WriteOffFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  search?: string;
  status?: string;
  location_id?: string;
  reason?: string;
  date_from?: string;
  date_to?: string;
  sort?: string;
  include_summary?: boolean;
  include_items_preview?: boolean;
}

export interface WriteOffsResponse extends ListResponse<WriteOffListItem> {
  summary?: WriteOffListSummary | null;
}

export interface WriteOffDetailResponse {
  request_id: string;
  document: WriteOffDocument;
  current_revision?: WriteOffCurrentRevision | null;
  audit_events?: StockAuditEvent[];
  revisions?: WriteOffRevisionMetadata[];
  already_confirmed?: boolean;
  already_cancelled?: boolean;
}

export interface WriteOffItemRequest {
  product_id: string;
  quantity: string;
  unit_code: string;
}

/** Create write-off request */
export interface CreateWriteOffRequest {
  location_id: string;
  write_off_at: string;
  reason: WriteOffReason;
  items: WriteOffItemRequest[];
  notes?: string;
  initial_status?: 'draft' | 'confirmed';
}

export interface UpdateWriteOffDraftRequest {
  location_id: string;
  write_off_at: string;
  reason: WriteOffReason;
  items: WriteOffItemRequest[];
  notes?: string;
}

export interface ReviseWriteOffRequest extends UpdateWriteOffDraftRequest {
  base_revision_no: number;
  change_reason?: string;
}

export interface CancelWriteOffRequest {
  base_revision_no: number;
  change_reason?: string;
}

// =============================================================================
// Supplier Return Types
// =============================================================================

/** Supplier return header */
export interface SupplierReturn {
  id: string;
  location_id: string;
  supplier_id: string;
  status: SupplierReturnStatus;
  return_date: string;
  reason?: string | null;
  items_count?: number | null;
  unit_totals?: Record<string, string> | null;
  total_amount?: string | null;
  items?: SupplierReturnItem[] | null;
  created_at?: string | null;
  created_by?: string | null;
  current_revision_no?: number | null;
  current_revision_id?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_reason?: string | null;
}

/** Supplier return item */
export interface SupplierReturnItem {
  id: string;
  return_id: string;
  line_ref?: string | null;
  product_id: string;
  quantity: string;
  unit_code: string;
}

/** Supplier return with items */
export interface SupplierReturnDetail extends SupplierReturn {
  items: SupplierReturnItem[];
}

export interface SupplierReturnDetailResponse {
  request_id: string;
  supplier_return: SupplierReturnDetail;
  audit_events?: StockAuditEvent[] | null;
  already_confirmed?: boolean;
  already_cancelled?: boolean;
}

/** Supplier return filters */
export interface SupplierReturnFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  location_id?: string;
  supplier_id?: string;
  status?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

/** Supplier returns response */
export type SupplierReturnsResponse = ListResponse<SupplierReturn>;

/** Create supplier return request */
export interface CreateSupplierReturnRequest {
  location_id: string;
  supplier_id: string;
  return_date: string;
  reason?: string;
  /** Defaults to "draft" on backend. */
  initial_status?: SupplierReturnStatus;
  items: CreateSupplierReturnItemRequest[];
}

/** Create supplier return item */
export interface CreateSupplierReturnItemRequest {
  product_id: string;
  quantity: string;
  unit_code: string;
}

/** Update draft supplier return request */
export interface UpdateSupplierReturnRequest {
  location_id: string;
  supplier_id: string;
  return_date: string;
  reason?: string;
  items: CreateSupplierReturnItemRequest[];
}

export interface ReviseSupplierReturnRequest extends UpdateSupplierReturnRequest {
  change_reason?: string;
}

export interface CancelSupplierReturnRequest {
  cancel_reason: string;
}

export interface DeleteSupplierReturnResponse {
  request_id: string;
  deleted: boolean;
}

// =============================================================================
// Production Types
// =============================================================================

export interface ProductionItemPreview {
  product_id: string;
  product_name: string;
  quantity: string;
  unit_code: string;
}

/** Production header */
export interface Production {
  id: string;
  location_id: string;
  production_date: string;
  status: ProductionStatus;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_reason?: string | null;
  notes?: string | null;
  unit_totals?: Record<string, string> | null;
  estimated_total_amount?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

/** Production list item */
export interface ProductionListItem extends Production {
  items_count: number;
  items_preview?: ProductionItemPreview[] | null;
}

/** Production item */
export interface ProductionItem {
  id: string;
  production_id: string;
  product_id: string;
  quantity: string;
  unit_code: string;
  recipe_id?: string | null;
  consumption_items?: ProductionConsumptionItem[] | null;
}

export interface ProductionConsumptionItem {
  ingredient_product_id: string;
  recipe_quantity: string;
  actual_quantity: string;
  unit_code: string;
  is_override: boolean;
}

/** Production with items */
export interface ProductionDetail extends Production {
  items: ProductionItem[];
  items_count?: number | null;
}

export interface ProductionDetailResponse {
  request_id: string;
  production: ProductionDetail;
  audit_events?: StockAuditEvent[];
  already_confirmed?: boolean;
  already_cancelled?: boolean;
}

/** Production filters */
export interface ProductionFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  search?: string;
  location_id?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  sort?: string;
  include_summary?: boolean;
  include_items_preview?: boolean;
}

export interface ProductionListSummary {
  by_status: Record<string, number>;
}

/** Productions response */
export interface ProductionsResponse extends ListResponse<ProductionListItem> {
  summary?: ProductionListSummary | null;
}

/** Create production request */
export interface CreateProductionRequest {
  location_id: string;
  production_date: string;
  notes?: string;
  items: CreateProductionItemRequest[];
  initial_status?: 'draft' | 'confirmed';
}

export interface UpdateProductionRequest {
  location_id: string;
  production_date: string;
  notes?: string;
  items: CreateProductionItemRequest[];
  change_reason?: string;
}

/** Create production item */
export interface CreateProductionItemRequest {
  product_id: string;
  quantity: string;
  unit_code: string;
  recipe_id?: string;
  consumption_items?: ProductionConsumptionItemRequest[];
}

export interface ProductionConsumptionItemRequest {
  ingredient_product_id: string;
  recipe_quantity: string;
  actual_quantity: string;
  unit_code: string;
  is_override: boolean;
}

export interface CancelProductionRequest {
  cancel_reason: string;
}

// =============================================================================
// B2B Types
// =============================================================================

/** B2B Customer */
export interface B2BCustomer {
  id: string;
  /** Legacy/canonical full legal name. */
  name: string;
  full_name?: string | null;
  short_name?: string | null;
  display_name?: string | null;
  contact_person?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  delivery_addresses?: string[];
  delivery_address?: string | null;
  nip?: string | null;
  is_active: boolean;
  default_payment_term_days?: number | null;
  invoicing?: B2BSaleInvoicing;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface B2BCustomerDetailResponse {
  request_id: string;
  customer: B2BCustomer;
}

/** B2B Price List */
export interface B2BPriceList {
  id: string;
  customer_id: string;
  product_id: string;
  price: string;
  valid_from: string;
  valid_to?: string | null;
  created_at?: string | null;
}

/** Origin of an effective B2B price (MD-0104): the customer's own row or the standard price list. */
export type B2BPriceSource = 'customer' | 'standard';

/** The price a product is sold at to a customer on a date (MD-0104). */
export interface B2BEffectivePrice {
  product_id: string;
  price: string;
  valid_from: string;
  valid_to?: string | null;
  source: B2BPriceSource;
  /** Standard price list label, e.g. "Cennik hurtowy 15.09.2026"; null for customer rows. */
  source_note?: string | null;
}

export interface B2BEffectivePricesResponse {
  request_id: string;
  on_date: string;
  items: B2BEffectivePrice[];
}

/** Company wholesale (standard) price version (MD-0104). */
export interface B2BStandardPrice {
  id: string;
  product_id: string;
  product_name?: string | null;
  unit_code?: string | null;
  is_weighted?: boolean | null;
  price: string;
  valid_from: string;
  valid_to?: string | null;
  source?: string | null;
  created_at?: string | null;
}

export interface B2BStandardPricesResponse {
  request_id: string;
  items: B2BStandardPrice[];
}

/** B2B Customer filters */
export interface B2BCustomerFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  search?: string;
  is_active?: boolean;
}

/** B2B Customers response */
export type B2BCustomersResponse = ListResponse<B2BCustomer>;

export interface B2BCustomerStatsResponse {
  request_id: string;
  sales_count: number;
  sales_total_amount: string;
  last_sale_date: string | null;
  unpaid_count: number;
  unpaid_total_amount: string;
  overdue_count: number;
  overdue_total_amount: string;
}

export interface B2BCustomerNipLookupCustomer {
  name: string;
  nip: string;
  status_vat?: string | null;
  regon?: string | null;
  krs?: string | null;
  address?: string | null;
  working_address?: string | null;
  residence_address?: string | null;
  account_numbers: string[];
  registration_legal_date?: string | null;
}

export interface B2BCustomerNipLookupResponse {
  request_id: string;
  found: boolean;
  lookup_date: string;
  kas_request_id?: string | null;
  kas_request_datetime?: string | null;
  customer?: B2BCustomerNipLookupCustomer | null;
}

/** B2B Price List filters */
export interface B2BPriceListFilters extends PaginationParams {
  [key: string]: string | number | boolean | undefined;
  product_id?: string;
}

/** B2B Price Lists response */
export interface B2BPriceListsResponse {
  request_id: string;
  items: B2BPriceList[];
}

/** Create B2B Customer request */
export interface CreateB2BCustomerRequest {
  name?: string;
  full_name?: string;
  short_name?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  delivery_addresses?: string[];
  delivery_address?: string;
  nip?: string;
  default_payment_term_days?: number | null;
  invoicing?: B2BSaleInvoicing;
}

/** Update B2B Customer request */
export interface UpdateB2BCustomerRequest {
  name?: string;
  full_name?: string;
  short_name?: string | null;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string | null;
  delivery_addresses?: string[];
  delivery_address?: string | null;
  nip?: string;
  is_active?: boolean;
  default_payment_term_days?: number | null;
  invoicing?: B2BSaleInvoicing;
}

// =============================================================================
// Location Type (for lookups)
// =============================================================================

/** Location for dropdown */
export interface Location {
  id: string;
  name: string;
  code: string | null;
  is_active: boolean;
}

/** Locations response */
export interface LocationsResponse {
  items: Location[];
}

// =============================================================================
// Location Tracking Types
// =============================================================================

/** Location tracking status */
export interface LocationTrackingStatus {
  location_id: string;
  location_name: string;
  is_active: boolean;
  is_tracking_enabled: boolean;
  tracking_started_at: string | null;
  start_date: string | null;
  first_inventory_at: string | null;
  requires_inventory: boolean;
}

/** Locations tracking response */
export interface LocationsTrackingResponse {
  request_id: string;
  items: LocationTrackingStatus[];
  total: number;
  tracking_count: number;
  not_tracking_count: number;
}

/** Enable tracking request */
export interface EnableTrackingRequest {
  start_date: string;
  start_from_zero?: boolean;
}

/** Location tracking detail response (get single) */
export interface LocationTrackingDetailResponse {
  request_id: string;
  location: LocationTrackingStatus;
}

/** Location tracking action response (enable/disable) */
export interface LocationTrackingActionResponse {
  request_id: string;
  location: LocationTrackingStatus;
  already_enabled?: boolean;
  already_disabled?: boolean;
}

// =============================================================================
// API Response Types
// =============================================================================

/** Success response */
/** Created response with ID */
export interface CreatedResponse {
  id: string;
  success: boolean;
}

// =============================================================================
// Product Costs (Stock UI)
// =============================================================================

export interface StockProductCostInfo {
  unit_cost: string;
  movement_type?: string | null;
  cost_source: string;
  is_partial: boolean;
  effective_at?: string | null;
}

export interface StockProductCostsResponse {
  request_id: string;
  costs: Record<string, StockProductCostInfo | null>;
}

export interface StockProductTotalsResponse {
  request_id: string;
  totals: Record<string, string>;
}

async function getLatestMergedStockProductCosts(
  productIds: string[] = [],
): Promise<StockProductCostsResponse> {
  const ids = Array.from(new Set(productIds.filter(Boolean))).sort();
  if (ids.length === 0) return { request_id: 'skip', costs: {} };

  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += 100) {
    chunks.push(ids.slice(index, index + 100));
  }

  const results: StockProductCostsResponse[] = await Promise.all(
    chunks.map(
      (chunk): Promise<StockProductCostsResponse> =>
        api.webAdmin.get('/stock/products/costs', {
          params: {
            product_ids: chunk.join(','),
          },
        }),
    ),
  );
  const costs: Record<string, StockProductCostInfo | null> = {};
  for (const result of results) {
    Object.assign(costs, result.costs);
  }
  return { request_id: results[0]?.request_id ?? 'merged', costs };
}

// =============================================================================
// Default Limits
// =============================================================================

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

const stockProductsApi = createStockProductsApi();
const stockSupplierProductMappingsApi = createStockSupplierProductMappingsApi();

// =============================================================================
// API Functions
// =============================================================================

/**
 * Stock API object with all endpoint functions.
 */
export const stockApi = {
  // -------------------------------------------------------------------------
  // Balances
  // -------------------------------------------------------------------------
  balances: stockProductsApi,

  // -------------------------------------------------------------------------
  // Products
  // -------------------------------------------------------------------------
  products: stockProductsApi,

  // -------------------------------------------------------------------------
  // Product costs (latest unit costs by product)
  // -------------------------------------------------------------------------
  productCosts: {
    getLatest: (productIds: string[] = []): Promise<StockProductCostsResponse> =>
      api.webAdmin.get('/stock/products/costs', {
        params: {
          product_ids: productIds.filter(Boolean).join(','),
        },
      }),
    getLatestMerged: getLatestMergedStockProductCosts,
    getLatestBatch: (productIds: string[] = []): Promise<StockProductCostsResponse> =>
      api.webAdmin.post('/stock/products/costs/batch', {
        product_ids: productIds.filter(Boolean),
      }),
  },

  productTotals: {
    getLatestBatch: (productIds: string[] = []): Promise<StockProductTotalsResponse> =>
      api.webAdmin.post('/stock/products/totals', {
        product_ids: productIds.filter(Boolean),
      }),
  },

  sellerWorkspace: createStockSellerWorkspaceApi(),

  // -------------------------------------------------------------------------
  // Operations (Операции) - read-only feed
  // -------------------------------------------------------------------------
  operations: {
    list: (params: StockOperationsListParams = {}): Promise<StockOperationsListResponse> =>
      api.webAdmin.get('/stock/operations', {
        params: {
          limit: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),

    detail: (operationId: string): Promise<StockOperationDetailResponse> =>
      api.webAdmin.get(`/stock/operations/${operationId}`),

    viewCounts: (
      params: {
        include_cancelled?: boolean;
        q?: string;
        location_id?: string;
        product_id?: string;
        entity_type?: string[] | string;
      } = {},
    ): Promise<StockOperationsViewCountsResponse> =>
      api.webAdmin.get('/stock/operations/view-counts', { params }),
  },

  // -------------------------------------------------------------------------
  // Movements
  // -------------------------------------------------------------------------
  movements: {
    list: (filters: MovementFilters = {}): Promise<MovementsResponse> =>
      api.webAdmin.get('/stock/movements', { params: filters }),

    dailySummary: (params: {
      product_id: string;
      location_id?: string | null;
      movement_type?: string[] | string;
      date_from: string;
      date_to: string;
    }): Promise<MovementsDailySummaryResponse> =>
      api.webAdmin.get('/stock/movements/daily-summary', { params }),

    salesStats: (params: {
      product_id: string;
      date_from: string;
      date_to: string;
    }): Promise<SalesStatsResponse> => api.webAdmin.get('/stock/movements/sales-stats', { params }),

    productSalesDashboard: (params: {
      product_id: string;
      days: number;
    }): Promise<ProductSalesDashboardResponse> =>
      api.webAdmin.get('/stock/movements/product-sales-dashboard', { params }),

    productProductionDashboard: (params: {
      product_id: string;
      days: number;
    }): Promise<ProductProductionDashboardResponse> =>
      api.webAdmin.get('/stock/movements/product-production-dashboard', { params }),

    detail: (id: string): Promise<MovementDetailResponse> =>
      api.webAdmin.get(`/stock/movements/${id}`),

    /** Cancels a manual adjustment; document lines change only through their document (MD-0093). */
    cancel: (id: string): Promise<MovementDetailResponse> =>
      api.webAdmin.post(`/stock/movements/${id}/cancel`),

    /** Manual adjustment: the only ledger change without a document (MD-0093). */
    manualCorrection: (data: ManualCorrectionRequest): Promise<MovementDetailResponse> =>
      api.webAdmin.post('/stock/movements/manual-correction', data),
  },

  // -------------------------------------------------------------------------
  // Receipts
  // -------------------------------------------------------------------------
  receipts: createStockReceiptsApi({
    rememberStockDocumentUpdatedAt,
    forgetStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // Supplier product mappings
  // -------------------------------------------------------------------------
  supplierProductMappings: stockSupplierProductMappingsApi,

  // -------------------------------------------------------------------------
  // Transfers
  // -------------------------------------------------------------------------
  transfers: createStockTransfersApi({
    rememberStockDocumentUpdatedAt,
    forgetStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // Recipes
  // -------------------------------------------------------------------------
  recipes: createStockRecipesApi({
    rememberStockDocumentUpdatedAt,
    forgetStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // Labor Rates
  // -------------------------------------------------------------------------
  laborRates: createStockLaborRatesApi(),
  overheadRates: createStockOverheadRatesApi(),

  // -------------------------------------------------------------------------
  // Ingredients
  // -------------------------------------------------------------------------
  ingredients: createStockIngredientsApi({
    rememberStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // Supplies (TMC)
  // -------------------------------------------------------------------------
  supplies: createStockSuppliesApi({
    rememberStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // Materials (unified ingredients + supplies)
  // -------------------------------------------------------------------------
  materials: createStockMaterialsApi(),

  // -------------------------------------------------------------------------
  // Write-offs
  // -------------------------------------------------------------------------
  writeOffs: {
    list: async (filters: WriteOffFilters = {}): Promise<WriteOffsResponse> => {
      return rememberResponseItemsUpdatedAt(
        rememberStockDocumentUpdatedAt,
        'write_off',
        await api.webAdmin.get<WriteOffsResponse>('/stock/write-offs', {
          params: filters,
        }),
        (response) => response.items,
        (item) => item.document_id,
        (item) => item.updated_at,
      );
    },

    detail: async (id: string): Promise<WriteOffDetailResponse> => {
      return rememberResponseUpdatedAt(
        rememberStockDocumentUpdatedAt,
        'write_off',
        await api.webAdmin.get<WriteOffDetailResponse>(`/stock/write-offs/${id}`),
        (response) => response.document.document_id,
        (response) => response.document.updated_at,
      );
    },

    create: (
      data: CreateWriteOffRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<WriteOffDetailResponse> =>
      api.webAdmin
        .post<WriteOffDetailResponse>('/stock/write-offs', data, options)
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'write_off',
            response,
            (current) => current.document.document_id,
            (current) => current.document.updated_at,
          ),
        ),

    updateDraft: (
      documentId: string,
      data: UpdateWriteOffDraftRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<WriteOffDetailResponse> =>
      api.webAdmin
        .put<WriteOffDetailResponse>(
          `/stock/write-offs/${documentId}`,
          data,
          withExpectedUpdatedAt('write_off', documentId, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'write_off',
            response,
            (current) => current.document.document_id,
            (current) => current.document.updated_at,
          ),
        ),

    confirm: (
      documentId: string,
      options?: StockDocumentMutationOptions,
    ): Promise<WriteOffDetailResponse> =>
      api.webAdmin
        .post<WriteOffDetailResponse>(
          `/stock/write-offs/${documentId}/confirm`,
          undefined,
          withExpectedUpdatedAt('write_off', documentId, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'write_off',
            response,
            (current) => current.document.document_id,
            (current) => current.document.updated_at,
          ),
        ),

    revise: (
      documentId: string,
      data: ReviseWriteOffRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<WriteOffDetailResponse> =>
      api.webAdmin
        .post<WriteOffDetailResponse>(
          `/stock/write-offs/${documentId}/revise`,
          data,
          withExpectedUpdatedAt('write_off', documentId, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'write_off',
            response,
            (current) => current.document.document_id,
            (current) => current.document.updated_at,
          ),
        ),

    cancel: (
      documentId: string,
      data: CancelWriteOffRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<WriteOffDetailResponse> =>
      api.webAdmin
        .post<WriteOffDetailResponse>(
          `/stock/write-offs/${documentId}/cancel`,
          data,
          withExpectedUpdatedAt('write_off', documentId, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'write_off',
            response,
            (current) => current.document.document_id,
            (current) => current.document.updated_at,
          ),
        ),

    deleteDraft: (
      documentId: string,
      options?: StockDocumentMutationOptions,
    ): Promise<{ success: boolean }> =>
      api.webAdmin
        .delete<{
          success: boolean;
        }>(
          `/stock/write-offs/${documentId}`,
          withExpectedUpdatedAt('write_off', documentId, options),
        )
        .then((response) => {
          forgetStockDocumentUpdatedAt('write_off', documentId);
          return response;
        }),
  },

  // -------------------------------------------------------------------------
  // Supplier Returns
  // -------------------------------------------------------------------------
  supplierReturns: {
    list: async (filters: SupplierReturnFilters = {}): Promise<SupplierReturnsResponse> => {
      return rememberResponseItemsUpdatedAt(
        rememberStockDocumentUpdatedAt,
        'supplier_return',
        await api.webAdmin.get<SupplierReturnsResponse>('/stock/supplier-returns', {
          params: filters,
        }),
        (response) => response.items,
        (item) => item.id,
        (item) => item.updated_at,
      );
    },

    detail: async (id: string): Promise<SupplierReturnDetailResponse> => {
      return rememberResponseUpdatedAt(
        rememberStockDocumentUpdatedAt,
        'supplier_return',
        await api.webAdmin.get<SupplierReturnDetailResponse>(`/stock/supplier-returns/${id}`),
        (response) => response.supplier_return.id,
        (response) => response.supplier_return.updated_at,
      );
    },

    create: (
      data: CreateSupplierReturnRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<SupplierReturnDetailResponse> =>
      api.webAdmin
        .post<SupplierReturnDetailResponse>('/stock/supplier-returns', data, options)
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'supplier_return',
            response,
            (current) => current.supplier_return.id,
            (current) => current.supplier_return.updated_at,
          ),
        ),

    updateDraft: (
      id: string,
      data: UpdateSupplierReturnRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<SupplierReturnDetailResponse> =>
      api.webAdmin
        .put<SupplierReturnDetailResponse>(
          `/stock/supplier-returns/${id}`,
          data,
          withExpectedUpdatedAt('supplier_return', id, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'supplier_return',
            response,
            (current) => current.supplier_return.id,
            (current) => current.supplier_return.updated_at,
          ),
        ),

    revise: (
      id: string,
      data: ReviseSupplierReturnRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<SupplierReturnDetailResponse> =>
      api.webAdmin
        .post<SupplierReturnDetailResponse>(
          `/stock/supplier-returns/${id}/revise`,
          data,
          withExpectedUpdatedAt('supplier_return', id, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'supplier_return',
            response,
            (current) => current.supplier_return.id,
            (current) => current.supplier_return.updated_at,
          ),
        ),

    confirm: (
      id: string,
      options?: StockDocumentMutationOptions,
    ): Promise<SupplierReturnDetailResponse> =>
      api.webAdmin
        .post<SupplierReturnDetailResponse>(
          `/stock/supplier-returns/${id}/confirm`,
          undefined,
          withExpectedUpdatedAt('supplier_return', id, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'supplier_return',
            response,
            (current) => current.supplier_return.id,
            (current) => current.supplier_return.updated_at,
          ),
        ),

    cancel: (
      id: string,
      data: CancelSupplierReturnRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<SupplierReturnDetailResponse> =>
      api.webAdmin
        .post<SupplierReturnDetailResponse>(
          `/stock/supplier-returns/${id}/cancel`,
          data,
          withExpectedUpdatedAt('supplier_return', id, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'supplier_return',
            response,
            (current) => current.supplier_return.id,
            (current) => current.supplier_return.updated_at,
          ),
        ),

    deleteDraft: (
      id: string,
      options?: StockDocumentMutationOptions,
    ): Promise<DeleteSupplierReturnResponse> =>
      api.webAdmin
        .delete<DeleteSupplierReturnResponse>(
          `/stock/supplier-returns/${id}`,
          withExpectedUpdatedAt('supplier_return', id, options),
        )
        .then((response) => {
          forgetStockDocumentUpdatedAt('supplier_return', id);
          return response;
        }),
  },

  // -------------------------------------------------------------------------
  // Productions
  // -------------------------------------------------------------------------
  productions: {
    list: async (filters: ProductionFilters = {}): Promise<ProductionsResponse> => {
      return rememberResponseItemsUpdatedAt(
        rememberStockDocumentUpdatedAt,
        'production',
        await api.webAdmin.get<ProductionsResponse>('/stock/productions', {
          params: filters,
        }),
        (response) => response.items,
        (item) => item.id,
        (item) => item.updated_at,
      );
    },

    detail: async (id: string): Promise<ProductionDetailResponse> => {
      return rememberResponseUpdatedAt(
        rememberStockDocumentUpdatedAt,
        'production',
        await api.webAdmin.get<ProductionDetailResponse>(`/stock/productions/${id}`),
        (response) => response.production.id,
        (response) => response.production.updated_at,
      );
    },

    create: (
      data: CreateProductionRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<ProductionDetailResponse> =>
      api.webAdmin
        .post<ProductionDetailResponse>('/stock/productions', data, options)
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'production',
            response,
            (current) => current.production.id,
            (current) => current.production.updated_at,
          ),
        ),

    update: (
      id: string,
      data: UpdateProductionRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<ProductionDetailResponse> =>
      api.webAdmin
        .put<ProductionDetailResponse>(
          `/stock/productions/${id}`,
          data,
          withExpectedUpdatedAt('production', id, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'production',
            response,
            (current) => current.production.id,
            (current) => current.production.updated_at,
          ),
        ),

    confirm: (
      id: string,
      options?: StockDocumentMutationOptions,
    ): Promise<ProductionDetailResponse> =>
      api.webAdmin
        .post<ProductionDetailResponse>(
          `/stock/productions/${id}/confirm`,
          undefined,
          withExpectedUpdatedAt('production', id, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'production',
            response,
            (current) => current.production.id,
            (current) => current.production.updated_at,
          ),
        ),

    cancel: (
      id: string,
      data: CancelProductionRequest,
      options?: StockDocumentMutationOptions,
    ): Promise<ProductionDetailResponse> =>
      api.webAdmin
        .post<ProductionDetailResponse>(
          `/stock/productions/${id}/cancel`,
          data,
          withExpectedUpdatedAt('production', id, options),
        )
        .then((response) =>
          rememberResponseUpdatedAt(
            rememberStockDocumentUpdatedAt,
            'production',
            response,
            (current) => current.production.id,
            (current) => current.production.updated_at,
          ),
        ),

    deleteDraft: (
      id: string,
      options?: StockDocumentMutationOptions,
    ): Promise<{ success: boolean }> =>
      api.webAdmin
        .delete<{
          success: boolean;
        }>(`/stock/productions/${id}`, withExpectedUpdatedAt('production', id, options))
        .then((response) => {
          forgetStockDocumentUpdatedAt('production', id);
          return response;
        }),
  },

  // -------------------------------------------------------------------------
  // Inventories
  // -------------------------------------------------------------------------
  inventories: createStockInventoriesApi({
    rememberStockDocumentUpdatedAt,
    forgetStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // B2B Customers
  // -------------------------------------------------------------------------
  b2bCustomers: {
    list: (filters: B2BCustomerFilters = {}): Promise<B2BCustomersResponse> =>
      api.webAdmin.get('/stock/b2b-customers', { params: filters }),

    detail: (id: string): Promise<B2BCustomerDetailResponse> =>
      api.webAdmin.get(`/stock/b2b-customers/${id}`),

    stats: (id: string): Promise<B2BCustomerStatsResponse> =>
      api.webAdmin.get(`/stock/b2b-customers/${id}/stats`),

    lookupByNip: (nip: string, lookupDate?: string): Promise<B2BCustomerNipLookupResponse> =>
      api.webAdmin.get('/stock/b2b-customers/nip-lookup', {
        params: {
          nip,
          ...(lookupDate ? { date: lookupDate } : {}),
        },
      }),

    create: (data: CreateB2BCustomerRequest): Promise<B2BCustomerDetailResponse> =>
      api.webAdmin.post('/stock/b2b-customers', data),

    update: (id: string, data: UpdateB2BCustomerRequest): Promise<B2BCustomerDetailResponse> =>
      api.webAdmin.put(`/stock/b2b-customers/${id}`, data),

    delete: (id: string): Promise<SuccessResponse> =>
      api.webAdmin.delete(`/stock/b2b-customers/${id}`),
  },

  // -------------------------------------------------------------------------
  // B2B Customer Prices
  // -------------------------------------------------------------------------
  b2bPrices: {
    list: (customerId: string, filters: B2BPriceListFilters = {}): Promise<B2BPriceListsResponse> =>
      api.webAdmin.get(`/stock/b2b-customers/${customerId}/prices`, { params: filters }),

    upsert: (
      customerId: string,
      items: {
        product_id: string;
        price: string;
        valid_from: string;
        valid_to?: string | null;
      }[],
    ): Promise<B2BPriceListsResponse> =>
      api.webAdmin.put(`/stock/b2b-customers/${customerId}/prices`, items),

    /** Customer price, else standard price, per product on a date (default: today, Warsaw). */
    effective: (customerId: string, onDate?: string): Promise<B2BEffectivePricesResponse> =>
      api.webAdmin.get(`/stock/b2b-customers/${customerId}/effective-prices`, {
        params: onDate ? { on_date: onDate } : {},
      }),
  },

  // -------------------------------------------------------------------------
  // B2B Standard (wholesale) price list, MD-0104. Writes are API-only for now.
  // -------------------------------------------------------------------------
  b2bStandardPrices: {
    list: (): Promise<B2BStandardPricesResponse> => api.webAdmin.get('/stock/b2b-standard-prices'),
  },

  // -------------------------------------------------------------------------
  // B2B Sales
  // -------------------------------------------------------------------------
  b2bSales: createStockB2BSalesApi({
    rememberStockDocumentUpdatedAt,
    forgetStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // B2B Shipments
  // -------------------------------------------------------------------------
  b2bShipments: createStockB2BShipmentsApi({
    rememberStockDocumentUpdatedAt,
    forgetStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  }),

  // -------------------------------------------------------------------------
  // Sales invoices (MD-0091)
  // -------------------------------------------------------------------------
  salesInvoices: salesInvoicesApi,
  platformSales: platformSalesApi,
  hotReports: hotReportsApi,

  // -------------------------------------------------------------------------
  // Location Tracking
  // -------------------------------------------------------------------------
  locationTracking: {
    list: (): Promise<LocationsTrackingResponse> => api.webAdmin.get('/stock/location-tracking'),

    get: (locationId: string): Promise<LocationTrackingDetailResponse> =>
      api.webAdmin.get(`/stock/location-tracking/${locationId}`),

    enable: (
      locationId: string,
      startDate: string,
      startFromZero = false,
    ): Promise<LocationTrackingActionResponse> =>
      api.webAdmin.post(`/stock/location-tracking/${locationId}/enable`, {
        start_date: startDate,
        start_from_zero: startFromZero,
      }),

    disable: (locationId: string): Promise<LocationTrackingActionResponse> =>
      api.webAdmin.post(`/stock/location-tracking/${locationId}/disable`),
  },

  // -------------------------------------------------------------------------
  // Locations (for dropdowns)
  // -------------------------------------------------------------------------
  // TODO: Use references endpoints; stock module has no /locations route.
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch product metadata (names, categories, barcodes) for an inventory.
 * Used by detail page to show product names and enable barcode search.
 */
type InventoryQueryOptions = {
  enabled?: boolean;
  realtime?: boolean;
};

const INVENTORY_REALTIME_REFETCH_INTERVAL_MS = 15_000;

export function useInventoryProductsMeta(inventoryId?: string, options?: InventoryQueryOptions) {
  return useQuery({
    queryKey: queryKeys.stock.inventories.productsMeta(inventoryId ?? ''),
    queryFn: () => stockApi.inventories.productsMeta(inventoryId!),
    enabled: Boolean(inventoryId) && (options?.enabled ?? true),
  });
}

/**
 * Hook to fetch inventory detail by ID.
 * Used by the InventoryDetailPage.
 */
export function useInventoryDetail(inventoryId: string, options?: InventoryQueryOptions) {
  const realtime = options?.realtime ?? false;
  return useQuery({
    queryKey: queryKeys.stock.inventories.detail(inventoryId),
    queryFn: () => stockApi.inventories.detail(inventoryId),
    enabled: Boolean(inventoryId) && (options?.enabled ?? true),
    ...(realtime
      ? {
          staleTime: 0,
          refetchOnWindowFocus: true,
          refetchInterval: INVENTORY_REALTIME_REFETCH_INTERVAL_MS,
        }
      : {
          refetchOnWindowFocus: false,
          refetchInterval: false,
        }),
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook to update inventory items (actual quantities).
 * Invalidates the inventory detail after success.
 */
export function useUpdateInventoryItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      inventoryId,
      items,
    }: {
      inventoryId: string;
      items: UpdateInventoryItemRequest[];
    }) => stockApi.inventories.updateItems(inventoryId, items),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stock.inventories.detail(variables.inventoryId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.inventories.all });
    },
  });
}

/**
 * Hook to complete an inventory.
 * Invalidates inventory detail and list after success.
 */
export function useCompleteInventory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inventoryId: string) => stockApi.inventories.complete(inventoryId),
    onSuccess: (_data, inventoryId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.inventories.detail(inventoryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.inventories.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.balances.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.alerts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.movements.all });
    },
  });
}

/**
 * Hook to delete (cancel) an inventory.
 * Only allowed for status === 'in_progress'.
 * Invalidates inventory detail and list after success.
 */
export function useDeleteInventory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inventoryId: string) => stockApi.inventories.delete(inventoryId),
    onSuccess: (_data, inventoryId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.inventories.detail(inventoryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.inventories.all });
    },
  });
}
